// hooks/useLoopingAudio.js
//
// Plays one window of a track on a loop in the browser: an invitation's or a
// card's background music, and the preview in the music picker. The web twin
// of the app's features/music/useLoopingTrack.js.
//
// Browsers only let a page start sound after the visitor has interacted with
// it, so this reports `blocked` when a play is refused, and offers:
//   playNow()  start (unmuted) from inside a tap - the invitation's music line
//   unlock()   quietly unlock the element inside a tap, for music that starts
//              a moment later (a card, after its envelope opens)
//
// The loop runs on requestAnimationFrame while playing rather than on
// `timeupdate`, which fires only about four times a second - late enough to
// let the start of the next bar through at the end of the window.
import { useCallback, useEffect, useRef, useState } from 'react';
import { loopSeekTarget } from '../shared/backgroundMusic';

const FADE_STEP_MS = 60;

export default function useLoopingAudio(music, { playing, muted = false, volume = 0.75, fadeMs = 1400 } = {}) {
  const audioRef = useRef(null);
  if (!audioRef.current && typeof Audio !== 'undefined') {
    const a = new Audio();
    a.preload = 'auto';
    audioRef.current = a;
  }

  const musicRef = useRef(music);
  musicRef.current = music;
  const [ready, setReady] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [pageVisible, setPageVisible] = useState(
    typeof document === 'undefined' ? true : document.visibilityState !== 'hidden',
  );

  // Load.
  const url = music?.url ?? null;
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    setReady(false);
    if (!url) {
      a.pause();
      a.removeAttribute('src');
      return undefined;
    }
    const onMeta = () => {
      try { a.currentTime = musicRef.current?.start ?? 0; } catch { /* corrected by the loop */ }
      setReady(true);
    };
    a.addEventListener('loadedmetadata', onMeta);
    a.src = url;
    a.load();
    return () => a.removeEventListener('loadedmetadata', onMeta);
  }, [url]);

  // A moved window starts from its top.
  const start = music?.start ?? 0;
  const length = music?.length ?? 0;
  useEffect(() => {
    if (!ready || !audioRef.current) return;
    try { audioRef.current.currentTime = start; } catch { /* ignore */ }
  }, [start, length, ready]);

  // The loop.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    let raf = 0;
    const tick = () => {
      const target = loopSeekTarget(a.currentTime, musicRef.current);
      if (target !== null) {
        try { a.currentTime = target; } catch { /* next frame */ }
      }
      raf = requestAnimationFrame(tick);
    };
    const onPlay = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(tick); };
    const onPause = () => cancelAnimationFrame(raf);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    return () => {
      cancelAnimationFrame(raf);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
    };
  }, []);

  // A hidden tab goes quiet.
  useEffect(() => {
    const onVis = () => setPageVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = !!muted;
  }, [muted]);

  // Play / pause with the fade. (iOS Safari ignores `volume`, so there it
  // simply starts at full level; everywhere else it eases in.)
  const shouldPlay = !!playing && ready && pageVisible;
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    if (!shouldPlay) {
      a.pause();
      return undefined;
    }
    let level = 0;
    const steps = Math.max(1, Math.round(fadeMs / FADE_STEP_MS));
    try { a.volume = 0; } catch { /* read-only on iOS */ }
    const p = a.play();
    if (p && p.then) {
      p.then(() => setBlocked(false)).catch((e) => {
        if (e && e.name === 'NotAllowedError') setBlocked(true);
      });
    }
    const timer = setInterval(() => {
      level += 1;
      try { a.volume = Math.min(volume, (volume * level) / steps); } catch { /* ignore */ }
      if (level >= steps) clearInterval(timer);
    }, FADE_STEP_MS);
    return () => clearInterval(timer);
  }, [shouldPlay, volume, fadeMs]);

  // Stop for good on unmount.
  useEffect(() => () => {
    const a = audioRef.current;
    if (a) { a.pause(); a.removeAttribute('src'); }
  }, []);

  /** From inside a tap: start now. */
  const playNow = useCallback(() => {
    const a = audioRef.current;
    if (!a || !a.src) return;
    try { a.volume = volume; } catch { /* ignore */ }
    const p = a.play();
    if (p && p.then) p.then(() => setBlocked(false)).catch(() => {});
  }, [volume]);

  /** From inside a tap: unlock the element silently, for a later start. */
  const unlock = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    const wasMuted = a.muted;
    a.muted = true;
    const p = a.play();
    const restore = () => { a.pause(); a.muted = wasMuted; };
    if (p && p.then) p.then(restore).catch(() => { a.muted = wasMuted; });
    else restore();
  }, []);

  return { ready, blocked, playNow, unlock };
}
